import joi from 'joi';

import type { GraphPath } from '../GraphPath';
import type { EdgeId, TrafficScore } from '../edges';
import { type Point, pointSchema } from '../elements';
import type { Packet } from '../pathLookup/Packet';
import type { ReportSeverity } from '../report';
import type { Branded } from '../types/Branded';
import type { NodeStyle } from './NodeStyle';
import {
  type DeviceType,
  type NodeType,
  cloudConstructNodeTypes,
  cloudInstanceNodeTypes,
  cloudInternetGwNodeTypes,
  cloudNatGwNodeTypes,
  cloudNetworkInterfaceNodeTypes,
  cloudNetworkNodeTypes,
  cloudPeeringNodeTypes,
  cloudSubnetNodeTypes,
  deviceTypes,
  nodeTypes,
} from './constants';

export type BoxId = string;
export type NodeId = Branded<string, 'NodeId'>;
export type NodeSn = Branded<string, 'NodeSn'>;

export type NodeGraphType = 'topology' | 'pathLookup';
export type NodeGraphExtra = Record<string, string | number>; // extra info eg for host table filtering;

export interface NodeData {
  children: NodeId[];
  id: NodeId;
  intentCheckResult?: ReportSeverity | null;
  sn: NodeSn;
  label: string;
  position: Point;
  type: NodeType;
  graphType: NodeGraphType;
  boxId: BoxId | null; // boundary box ID
  parentPath: GraphPath | null; // path using which node has been loaded
  path?: GraphPath; // path in walk (not applicable to devices)
  stack?: boolean;
  style?: NodeStyle; // default blue
  extra: NodeGraphExtra | null;
}

export type UnpositionedNode = Omit<NodeData, 'position'>;

export interface TopologyNodeData extends NodeData {
  graphType: 'topology';
}

export type UnpositionedTopologyNode = Omit<TopologyNodeData, 'position'>;

export const isTopologyNodeData = (
  nodeData: NodeData,
): nodeData is TopologyNodeData => nodeData.graphType === 'topology';

interface InternalPacketData {
  ifaceName: string;
  packet: Packet;
}

export interface TerminatedPacketData extends InternalPacketData {
  prevEdgeIds: EdgeId[];
}

export type TerminatedPackets = {
  // terminated packets are refereced via edge ids too, but there are no corresponding edges are displayed in UI, the edges are internal
  [internalEdgeId in EdgeId]: TerminatedPacketData;
};

export interface GeneratedPacketData extends InternalPacketData {
  nextEdgeIds: EdgeId[];
  severityInfo: Record<ReportSeverity, number>;
  trafficScore: TrafficScore;
}

export type GeneratedPackets = {
  // generated packets are refereced via edge ids too, but there are no corresponding edges are displayed in UI, the edges are internal
  [internalEdgeId in EdgeId]: GeneratedPacketData;
};

export interface PathLookupNodeData extends NodeData {
  graphType: 'pathLookup';
  acceptedPackets?: TerminatedPackets;
  droppedPackets?: TerminatedPackets;
  generatedPackets?: GeneratedPackets;
}

export type UnpositionedPathLookupNode = Omit<PathLookupNodeData, 'position'>;

export const isPathLookupNodeData = (
  nodeData: NodeData,
): nodeData is PathLookupNodeData => nodeData.graphType === 'pathLookup';

export type NodePositions = {
  [node in NodeSn]?: Point;
};
export const nodePositionsSchema = joi
  .object()
  .pattern(joi.string(), pointSchema);

export type NodeRecord = Record<NodeId, NodeData>;

export function isDeviceType(type: NodeType): type is DeviceType {
  return (deviceTypes as readonly string[]).includes(type);
}

export function isCloudNetworkNodeType(type: NodeType): boolean {
  return (cloudNetworkNodeTypes as readonly string[]).includes(type);
}

export function isCloudNetworkInterfaceNodeType(type: NodeType): boolean {
  return (cloudNetworkInterfaceNodeTypes as readonly string[]).includes(type);
}

export function isCloudSubnetNodeType(type: NodeType): boolean {
  return (cloudSubnetNodeTypes as readonly string[]).includes(type);
}

export function isCloudPeeringNodeType(type: NodeType): boolean {
  return (cloudPeeringNodeTypes as readonly string[]).includes(type);
}

export function isCloudInstanceNodeType(type: NodeType): boolean {
  return (cloudInstanceNodeTypes as readonly string[]).includes(type);
}

export function isCloudNatGwNodeType(type: NodeType): boolean {
  return (cloudNatGwNodeTypes as readonly string[]).includes(type);
}

export function isCloudInternetGwNodeType(type: NodeType): boolean {
  return (cloudInternetGwNodeTypes as readonly string[]).includes(type);
}

export function isCloudConstructNodeType(type: NodeType): boolean {
  return (cloudConstructNodeTypes as readonly string[]).includes(type);
}

export type CloudConstructKind =
  | 'cloudNetwork'
  | 'networkInterface'
  | 'subnet'
  | 'peering'
  | 'instance'
  | 'natGateway'
  | 'internetGateway';

export function getCloudConstructKind(
  type: NodeType | null,
): CloudConstructKind | null {
  if (type === null) {
    return null;
  }
  if (isCloudNetworkNodeType(type)) {
    return 'cloudNetwork';
  }
  if (isCloudNetworkInterfaceNodeType(type)) {
    return 'networkInterface';
  }
  if (isCloudSubnetNodeType(type)) {
    return 'subnet';
  }
  if (isCloudPeeringNodeType(type)) {
    return 'peering';
  }
  if (isCloudInstanceNodeType(type)) {
    return 'instance';
  }
  if (isCloudNatGwNodeType(type)) {
    return 'natGateway';
  }
  if (isCloudInternetGwNodeType(type)) {
    return 'internetGateway';
  }
  return null;
}

export function isNodeType(type: string): type is NodeType {
  return (nodeTypes as readonly string[]).includes(type);
}

export type CloudConstructVendor = 'aws' | 'azure' | 'gcp';

// Cloud construct node type per (vendor, kind). Used to open the Device
// Explorer for a cloud construct from a table cell (NIM-24361). Mirrors the
// pathLookup graph convertor's icon table so both stay in sync. Returns null
// for unknown/unsupported vendors so callers can fall back to plain text.
const cloudConstructNodeTypeByVendor: {
  [vendor in CloudConstructVendor]: { [K in CloudConstructKind]?: NodeType };
} = {
  aws: {
    cloudNetwork: 'awsVpc',
    subnet: 'awsSubnet',
    networkInterface: 'awsEni',
    peering: 'awsVpcPeering',
    instance: 'awsInstance',
    natGateway: 'awsNatGw',
    internetGateway: 'awsIgw',
  },
  azure: {
    cloudNetwork: 'azureVnet',
    subnet: 'azureSubnet',
    networkInterface: 'azureNic',
    peering: 'azureVnetPeering',
    instance: 'azureInstance',
    natGateway: 'azureNat',
  },
  gcp: {
    cloudNetwork: 'gcpVpc',
    subnet: 'gcpSubnet',
    networkInterface: 'gcpNic',
    peering: 'gcpVpcPeering',
    instance: 'gcpInstance',
    natGateway: 'gcpCloudNat',
  },
};

export function cloudConstructNodeTypeFromVendor(
  vendor: string | null | undefined,
  kind: CloudConstructKind,
): NodeType | null {
  if (vendor === null || vendor === undefined) {
    return null;
  }
  return (
    cloudConstructNodeTypeByVendor[vendor as CloudConstructVendor]?.[kind] ??
    null
  );
}

export type BoxLabels = {
  [box in BoxId]?: string;
};

export interface CollapsedNodeGroup {
  nodeSns: NodeSn[];
  position: Point;
}
export const collapsedNodeGroupSchema = joi.object().keys({
  nodeSns: joi.array().items(joi.string().required()).required(),
  position: pointSchema.required(),
});
